import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { useCourses } from '@/features/courses/hooks/useCourses'
import { useCreateChallenge } from '../hooks/useChallenges'
import { CHALLENGE_DURATIONS, CHALLENGE_METRICS, type ChallengeMetric } from '../types'

const FIELD =
  'w-full rounded-lg border border-surface-200 px-3 py-2 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'

export function CreateChallengeForm() {
  const { t } = useTranslation()
  const [courseId, setCourseId] = useState('')
  const [metric, setMetric] = useState<ChallengeMetric>('sessions_completed')
  const [duration, setDuration] = useState<number>(7)

  const { data: coursesPage } = useCourses({})
  const create = useCreateChallenge()

  const courses = coursesPage?.data ?? []

  function handleSubmit(event: FormEvent) {
    event.preventDefault()

    if (courseId === '') return

    create.mutate(
      { course_id: courseId, metric, duration_days: duration },
      { onSuccess: () => setCourseId('') }
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <h3 className="font-display text-sm font-semibold text-on-surface">
        {t('challenges.create_title')}
      </h3>

      {create.isError && <Alert variant="error">{t('challenges.create_error')}</Alert>}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <span className="flex flex-col gap-1">
          <label htmlFor="challenge-course" className="text-xs font-medium text-on-surface-variant">
            {t('challenges.course')}
          </label>
          <select
            id="challenge-course"
            value={courseId}
            onChange={(event) => setCourseId(event.target.value)}
            className={FIELD}
          >
            <option value="">{t('challenges.course_placeholder')}</option>
            {courses.map((course) => (
              <option key={course.id} value={course.id}>
                {course.title}
              </option>
            ))}
          </select>
        </span>

        <span className="flex flex-col gap-1">
          <label htmlFor="challenge-metric" className="text-xs font-medium text-on-surface-variant">
            {t('challenges.metric')}
          </label>
          <select
            id="challenge-metric"
            value={metric}
            onChange={(event) => setMetric(event.target.value as ChallengeMetric)}
            className={FIELD}
          >
            {CHALLENGE_METRICS.map((value) => (
              <option key={value} value={value}>
                {t(`challenges.metrics.${value}`)}
              </option>
            ))}
          </select>
        </span>

        <span className="flex flex-col gap-1">
          <label
            htmlFor="challenge-duration"
            className="text-xs font-medium text-on-surface-variant"
          >
            {t('challenges.duration')}
          </label>
          <select
            id="challenge-duration"
            value={duration}
            onChange={(event) => setDuration(Number(event.target.value))}
            className={FIELD}
          >
            {CHALLENGE_DURATIONS.map((days) => (
              <option key={days} value={days}>
                {t('challenges.days', { count: days })}
              </option>
            ))}
          </select>
        </span>
      </div>

      <div>
        <Button type="submit" disabled={courseId === ''} isLoading={create.isPending}>
          {t('challenges.create')}
        </Button>
      </div>
    </form>
  )
}
